import React, { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import { zodResolver } from "@hookform/resolvers/zod";

import profileApi, { useCreateProfileMutation, useUpdateProfileMutation } from "../api/profile";
import { MfaSettings } from "../Components/Auth";
import HookFormTextInput from "../Components/Forms/TextInputs/HookFormTextInput";
import LoadingSpiner from "../Components/Loader/LoadingSpiner";
import ContentCard from "../Layouts/ContentCard";
import {
  loadData,
  profileSchema,
  ProfileType,
  updateData,
} from "../Store/profileSlice";
import { RootState } from "../Store/store";

export default function ProfilePage() {
  const [loaded, setLoaded] = useState(false);
  const dispatch = useDispatch();
  const authState = useSelector((state: RootState) => state.auth);
  const profileState = useSelector((state: RootState) => state.profile);

  const { refetch } =
    profileApi.endpoints.fetchProfileCurrent.useQuerySubscription({});
  const [createProfile] = useCreateProfileMutation();
  const [updateProfile] = useUpdateProfileMutation();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    control,
    formState: {
      errors,
      isSubmitSuccessful,
      isSubmitting,
      isSubmitted,
      submitCount,
    },
  } = useForm<ProfileType>({
    resolver: zodResolver(profileSchema),
  });

  useEffect(() => {
    refetch().then((response) => {
      if (response.isSuccess) {
        dispatch(loadData(response.data));
        // Reset form with the API data
        reset({
          userId: response.data.user_id,
          profile: {
            firstName: response.data.first_name || "",
            lastName: response.data.last_name || "",
            phone: response.data.phone || "",
            orgName: authState.orgName || "",
            subOrgName: response.data.sub_org_name || "",
            subSubOrgName: response.data.sub_sub_org_name || "",
          }
        });
      } else {
        // No existing profile, reset with empty values
        reset({
          userId: undefined,
          profile: {
            firstName: "",
            lastName: "",
            phone: "",
            orgName: authState.orgName || "",
            subOrgName: "",
            subSubOrgName: "",
          }
        });
      }
      setLoaded(true);
    });
  }, [refetch, authState.orgName, reset]);

  useEffect(() => {
    const changeSubscription = watch((data) => {
      dispatch(updateData(data));
    });
    return () => {
      changeSubscription.unsubscribe();
    };
  }, [watch]);

  if (!authState.isAuthenticated)
    return (
      <div className="container mx-auto">
        <ContentCard title="Authentication Error">
          <div className="alert alert-error">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>User is not authenticated.</span>
          </div>
        </ContentCard>
      </div>
    );

  if (!loaded || isSubmitting)
    return (
      <div className="container mx-auto">
        <ContentCard title="Loading Profile">
          <div className="flex justify-center items-center py-8">
            <LoadingSpiner />
            <span className="ml-2">Loading profile...</span>
          </div>
        </ContentCard>
      </div>
    );

  const onSubmit: SubmitHandler<ProfileType> = async (data) => {
    // Transform camelCase form data to snake_case API format
    const payload = {
      first_name: data.profile.firstName,
      last_name: data.profile.lastName,
      phone: data.profile.phone,
      sub_org_name: data.profile.subOrgName,
      sub_sub_org_name: data.profile.subSubOrgName,
    };
    
    try {
      if (profileState.userId) {
        // Update existing profile
        await updateProfile({ id: profileState.userId, body: payload }).unwrap();
      } else {
        // Create new profile
        await createProfile(payload).unwrap();
      }
      
      refetch().then((response) => {
        if (response.isSuccess) dispatch(loadData(response.data));
        else {
          reset(profileState);
          setValue("profile.orgName", authState.orgName);
        }
      });
    } catch (error) {
      console.error('Profile save error:', error);
    }
  };


  // Show the profile form
  return (
    <div className="container mx-auto">
      <ContentCard title={profileState.userId ? "Edit Profile" : "Complete Your Profile"}>
        {/* Success message banner */}
        {isSubmitSuccessful && (
          <div className="alert alert-success mb-6">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="font-bold">Profile Updated Successfully!</h3>
            </div>
          </div>
        )}
        
        <div className="mb-6">
          <p className="text-base-content/70">
            {profileState.userId 
              ? "Update your profile information below." 
              : "Please provide your information to get started with Pathbox."
            }
          </p>
        </div>
        
        <form
          className="space-y-8"
          onSubmit={handleSubmit(onSubmit, (errors) => {
            console.log('Form validation errors:', errors);
          })}
        >
          {/* Personal Information Section */}
          <div className="form-section">
            <h3 className="text-lg font-semibold text-base-content mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Personal Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <HookFormTextInput
                name="profile.firstName"
                label="First Name"
                register={register}
                errorMessage={errors.profile?.firstName?.message}
                required
              />
              <HookFormTextInput
                name="profile.lastName"
                label="Last Name"
                register={register}
                errorMessage={errors.profile?.lastName?.message}
                required
              />
              <HookFormTextInput
                name="profile.phone"
                label="Phone Number"
                register={register}
                placeholder="e.g., +1234567890, +44123456789, (555) 123-4567"
                errorMessage={errors.profile?.phone?.message}
                required
              />
            </div>
          </div>

          {/* Organization Information Section */}
          <div className="form-section">
            <h3 className="text-lg font-semibold text-base-content mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Organization Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <HookFormTextInput
                name="profile.orgName"
                label="Organization Name"
                register={register}
                disabled
                placeholder={authState.orgName || "Loading organization..."}
                errorMessage={errors.profile?.orgName?.message}
                required
              />
              <HookFormTextInput
                name="profile.subOrgName"
                label="Department"
                register={register}
                example="e.g., Pathology, R&D, Clinical Research, Laboratory Services"
                errorMessage={errors.profile?.subOrgName?.message}
                required
              />
              <HookFormTextInput
                name="profile.subSubOrgName"
                label="Team"
                register={register}
                example="e.g., Digital Pathology, Histopathology, Molecular Diagnostics, Research Lab"
                errorMessage={errors.profile?.subSubOrgName?.message}
                required
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-6 border-t border-base-300">
            <button
              type="submit"
              className={`btn btn-primary ${isSubmitting ? 'loading' : ''}`}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  Saving...
                </>
              ) : (
                'Save Profile'
              )}
            </button>
          </div>
        </form>
      </ContentCard>

      <ContentCard title="Security">
        <MfaSettings />
      </ContentCard>
    </div>
  );
}
